import type { PixelEvent } from '@/events';
import type { PixelLogger } from '@/logger/PixelLogger';

import type { DispatcherService } from './types';

type EventListener = (event: PixelEvent) => void;

const MAX_ERROR_MESSAGE_LENGTH = 200;

/**
 * Hardening 18.1 — falha de um listener não interrompe os demais nem propaga
 * para o produtor (trackers/engines). Log sanitizado, sem payload/PII.
 */
export class DispatcherServiceImpl implements DispatcherService {
  private readonly listeners = new Set<EventListener>();

  private lastEvent: PixelEvent | null = null;

  private readonly logger: Pick<PixelLogger, 'warn'> | undefined;

  constructor(logger?: Pick<PixelLogger, 'warn'>) {
    this.logger = logger;
  }

  dispatch(event: PixelEvent): void {
    this.lastEvent = event;

    // Snapshot: permite unsubscribe durante dispatch sem pular listeners.
    const snapshot = [...this.listeners];

    for (let index = 0; index < snapshot.length; index += 1) {
      const listener = snapshot[index];
      if (!listener) continue;

      try {
        listener(event);
      } catch (error) {
        this.logger?.warn(sanitizeDispatcherError(error, event.type, index));
      }
    }
  }

  subscribe(listener: (event: PixelEvent) => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  clear(): void {
    this.listeners.clear();
    this.lastEvent = null;
  }

  getListenerCount(): number {
    return this.listeners.size;
  }

  getLastEvent(): PixelEvent | null {
    return this.lastEvent;
  }
}

export function sanitizeDispatcherError(
  error: unknown,
  eventType: string,
  listenerIndex: number
): Record<string, unknown> {
  const name =
    error instanceof Error
      ? error.name
      : typeof error === 'object' && error !== null && 'name' in error
        ? String((error as { name: unknown }).name)
        : 'Error';

  let message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'unknown';

  message = message
    .replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[redacted]')
    .replace(/\+?\d[\d\s().-]{7,}\d/g, '[redacted]');

  if (message.length > MAX_ERROR_MESSAGE_LENGTH) {
    message = `${message.slice(0, MAX_ERROR_MESSAGE_LENGTH)}…`;
  }

  return {
    code: 'DISPATCHER_LISTENER_FAILED',
    eventType,
    listenerIndex,
    errorName: name,
    message,
    timestamp: Date.now(),
  };
}
